import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Trash2, Send, CheckCircle, AlertTriangle, ArrowLeft, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import ParticipationSheet from '../../components/syndication/ParticipationSheet';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const pct = (n) => `${Math.round((n || 0) * 100) / 100}%`;

const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-600', REQUESTED: 'bg-blue-100 text-blue-700',
  CONFIRMED: 'bg-emerald-100 text-emerald-700', ADJUSTED: 'bg-amber-100 text-amber-700',
  DECLINED: 'bg-red-100 text-red-700', FINALIZED: 'bg-purple-100 text-purple-700',
};

export default function SyndicationOffering() {
  const { dealId } = useParams();
  const { getAuthHeaders } = useAuth();
  const headers = useCallback(() => ({ ...getAuthHeaders(), 'X-Tenant': 'live' }), [getAuthHeaders]);

  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(true);
  const [commission, setCommission] = useState(0);
  const [fundingAmount, setFundingAmount] = useState(0);
  const [commForm, setCommForm] = useState({ commission_rate: '', broker_commission: '' });   // 10/28: attach it here
  const [offer, setOffer] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [availablePct, setAvailablePct] = useState(80);
  const [form, setForm] = useState({ syndicator_id: '', allocated_pct: '', role: 'INVESTOR', servicing_fee_rate: '', management_fee_pct: '' });
  const [busy, setBusy] = useState(false);
  const [openSheet, setOpenSheet] = useState({});   // 10/28: invitation id → participation sheet shown under the row

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, inv] = await Promise.all([
        axios.get(`${API}/syndication-offers/deal/${dealId}`, { headers: headers() }),
        axios.get(`${API}/syndication-offers/investors`, { headers: headers() }),
      ]);
      setInvestors(inv.data || []);
      if (o.data.offer) { setOffer(o.data.offer); setAvailablePct(o.data.offer.available_pct); }
      else {
        setOffer(null); setReady(!!o.data.ready_for_syndication); setCommission(o.data.commission || 0);
        setFundingAmount(Number(o.data.funding_amount) || 0);
        setCommForm({ commission_rate: o.data.commission_rate ?? '', broker_commission: o.data.broker_commission ?? '' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load offering');
    } finally { setLoading(false); }
  }, [dealId, headers]);

  useEffect(() => { load(); }, [load]);

  // 10/28 (Josh: "allow me to add the commission there as well … when the commission is added, it will just continue
  // on to the page"): PATCH the deal with what was typed (rate %, $ or both — the same fields as Full Edit) and reload;
  // the readiness gate then clears and "Open an offering" appears. Nothing else on the deal is touched.
  const rateNum = Number(commForm.commission_rate);
  const dollarNum = Number(commForm.broker_commission);
  const commissionPreview = dollarNum > 0 ? dollarNum : (rateNum > 0 ? fundingAmount * rateNum / 100 : 0);
  const saveCommission = async () => {
    const body = {};
    if (commForm.commission_rate !== '' && rateNum >= 0) body.commission_rate = rateNum;
    if (commForm.broker_commission !== '' && dollarNum >= 0) body.broker_commission = dollarNum;
    if (!(body.commission_rate > 0) && !(body.broker_commission > 0)) { toast.error('Enter a commission rate (%) or a dollar amount'); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/deals/${dealId}`, body, { headers: headers() });
      toast.success(`Commission attached: ${formatCurrency(commissionPreview)}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the commission'); } finally { setBusy(false); }
  };

  const createOffer = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/syndication-offers`, { deal_id: dealId, available_pct: Number(availablePct) }, { headers: headers() });
      setOffer(res.data); toast.success('Offer opened');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open offer');
    } finally { setBusy(false); }
  };

  const refresh = (data) => { if (data?.offer) setOffer(data.offer); else if (data?.id) setOffer(data); };

  const addInvitee = async () => {
    if (!form.syndicator_id || form.allocated_pct === '') { toast.error('Pick an investor and a %'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/syndication-offers/${offer.id}/invitations`, {
        syndicator_id: form.syndicator_id, offer_style: 'TARGET', allocated_pct: Number(form.allocated_pct),
        terms: { role: form.role, fee_income_share: form.role !== 'INVESTOR', fee_liability_share: true, servicing_fee_rate: Number(form.servicing_fee_rate || 0),
                 // 09/29 (Josh): management fee asked per participant; blank → legacy rule (2% non-partners on ISO deals)
                 management_fee_pct: form.management_fee_pct === '' ? null : Number(form.management_fee_pct) },
      }, { headers: headers() });
      refresh(res.data);
      setForm({ syndicator_id: '', allocated_pct: '', role: 'INVESTOR', servicing_fee_rate: '', management_fee_pct: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); } finally { setBusy(false); }
  };

  const removeInvitee = async (invId) => {
    setBusy(true);
    try { const res = await axios.delete(`${API}/syndication-offers/${offer.id}/invitations/${invId}`, { headers: headers() }); refresh(res.data); }
    catch (e) { toast.error('Remove failed'); } finally { setBusy(false); }
  };

  // 10/28: requests go out through the template email (TERMS block, personal link reused on a resend, "last emailed"
  // stamped) — the same path as the Needs Syndication page. One invitee or all of them.
  const sendTo = async (invs) => {
    const ids = invs.map(i => i.syndicator_id).filter(Boolean);
    if (!ids.length) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/syndication-offer-email/send`,
        { deal_id: offer.deal_id, offer_id: offer.id, recipient_contact_ids: ids }, { headers: headers() });
      const failed = (res.data.recipients || []).filter(r => !r.success);
      if (failed.length) toast.error(failed.map(r => `${r.name || r.contact_id}: ${r.error || 'failed'}`).join(' · '));
      if (res.data.sent) toast.success(ids.length === 1 ? `Request sent to ${invs[0].syndicator_name || 'the invitee'}` : `Requests sent: ${res.data.sent}, failed: ${res.data.failed || 0}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); } finally { setBusy(false); }
  };
  const sendRequests = () => sendTo(offer.invitations || []);

  // 10/28 (Josh: "a reset allocation button that will reset the current allocations and allow it to be sent out
  // again"): invitees + proposed % stay; answers, email counters and the personal links are wiped (old emails stop
  // working), the offer goes back to DRAFT. Refused once positions exist (CONFIRMED).
  const resetAllocation = async () => {
    const n = (offer.invitations || []).length;
    if (!window.confirm(`Reset this allocation round? ${n} invitee(s) keep their proposed % but every answer is cleared and the links in the earlier emails stop working. You can then send the requests again.`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/syndication-offers/${offer.id}/reset`, {}, { headers: headers() });
      refresh(res.data.offer);
      toast.success(`Allocation reset — ${res.data.reset} invitee(s) ready to be sent again`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); } finally { setBusy(false); }
  };

  const confirmOffer = async () => {
    if (!window.confirm('Confirm offering? This creates syndication records and opens the ledger.')) return;
    setBusy(true);
    try { const res = await axios.post(`${API}/syndication-offers/${offer.id}/confirm`, {}, { headers: headers() });
      refresh(res.data);
      const c = res.data.confirmations || {};
      toast.success(`Confirmed — ${res.data.syndications_created?.length || 0} syndications created · receipts emailed to ${c.sent || 0}${c.failed ? `, ${c.failed} failed` : ''}`);
      (c.results || []).filter((r) => !r.success).forEach((r) => toast.error(`Receipt to ${r.name || r.contact_id}: ${r.error || 'failed'}`));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Confirm failed'); } finally { setBusy(false); }
  };

  // 10/23 (Josh): an invitee's adjusted % is a request — approve (their % becomes final) or decline (original % stays).
  const decideAdjustment = async (inv, decision) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/syndication-offers/${offer.id}/invitations/${inv.id}/adjustment`, { decision }, { headers: headers() });
      refresh(res.data);
      toast.success(decision === 'approve' ? `Approved — ${inv.syndicator_name || 'invitee'} at ${inv.adjusted_pct}%` : `Declined — stays at ${inv.allocated_pct}%`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the decision'); } finally { setBusy(false); }
  };

  // 10/23 (Josh): the confirmation receipt (their %, money at that %, wire instructions) can be re-sent any time.
  // 10/29 (Josh: "a resend receipt option too"): per row (`invs` = [inv]) or everyone (no argument).
  const resendConfirmations = async (invs) => {
    setBusy(true);
    try {
      const body = invs && invs.length ? { syndicator_ids: invs.map((i) => i.syndicator_id) } : {};
      const res = await axios.post(`${API}/syndication-offers/${offer.id}/send-confirmations`, body, { headers: headers() });
      const who = invs && invs.length === 1 ? (invs[0].syndicator_name || 'the syndicator') : `${res.data.sent || 0}`;
      toast.success(`Confirmation receipt emailed to ${who}${res.data.failed ? `, ${res.data.failed} failed` : ''}`);
      (res.data.results || []).filter((r) => !r.success).forEach((r) => toast.error(`${r.name || r.contact_id}: ${r.error || 'failed'}`));
      await load();   // the row's "Resend receipt (×N)" count and last-sent time come from the invitation
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the receipts'); } finally { setBusy(false); }
  };

  if (loading) return <Layout><div className="p-8 text-slate-500">Loading offering…</div></Layout>;

  const comp = offer?.computation;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 space-y-4">
        <Link to={`/deals/${dealId}`} className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="w-4 h-4 mr-1" /> Back to deal</Link>
        <h1 className="text-2xl font-semibold">Syndication offering</h1>

        {!offer && !ready && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-800">Not ready for syndication</p>
                <p className="text-sm text-red-700">This deal has no commission attached ({formatCurrency(commission)}). Attach it here and continue.</p>
                <div className="mt-3 flex flex-wrap items-end gap-3" data-testid="attach-commission">
                  <div><Label htmlFor="offer_commission_rate">Commission rate (%)</Label>
                    <Input id="offer_commission_rate" type="number" step="0.01" min="0" className="w-32 bg-white" value={commForm.commission_rate}
                           onChange={(e) => setCommForm(f => ({ ...f, commission_rate: e.target.value }))} placeholder="e.g. 10" /></div>
                  <div><Label htmlFor="offer_commission_amount">Commission ($)</Label>
                    <Input id="offer_commission_amount" type="number" step="0.01" min="0" className="w-36 bg-white" value={commForm.broker_commission}
                           onChange={(e) => setCommForm(f => ({ ...f, broker_commission: e.target.value }))} placeholder="optional" /></div>
                  <div className="text-sm text-slate-600 pb-2">
                    = {formatCurrency(commissionPreview)} on {formatCurrency(fundingAmount)} funding
                    {dollarNum > 0 && rateNum > 0 && <span className="text-slate-400"> (the $ amount wins)</span>}
                  </div>
                  <Button size="sm" onClick={saveCommission} disabled={busy || commissionPreview <= 0}><CheckCircle className="w-4 h-4 mr-1" /> Save & continue</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!offer && ready && (
          <Card><CardHeader><CardTitle>Open an offering</CardTitle></CardHeader>
            <CardContent className="flex items-end gap-3">
              <div><Label>Available to syndicate %</Label><Input type="number" value={availablePct} onChange={(e) => setAvailablePct(e.target.value)} className="w-32" /></div>
              <Button onClick={createOffer} disabled={busy}>Open offering</Button>
            </CardContent>
          </Card>
        )}

        {offer && (
          <>
            <div className="flex items-center gap-3">
              <Badge className={STATUS_COLORS[offer.status] || ''}>{offer.status}</Badge>
              <span className="text-sm text-slate-500">Available {pct(offer.available_pct)} · payback {formatCurrency(offer.payback)} · orig fees {formatCurrency(offer.origination_fees)}</span>
            </div>

            {offer.status !== 'CONFIRMED' && (
              <Card><CardHeader><CardTitle>Allocate invitees</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-end">
                    <div className="md:col-span-2"><Label>Investor</Label>
                      <Select value={form.syndicator_id} onValueChange={(v) => setForm(f => ({ ...f, syndicator_id: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>{investors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div><Label>Proposed %</Label><Input type="number" value={form.allocated_pct} onChange={(e) => setForm(f => ({ ...f, allocated_pct: e.target.value }))} /></div>
                    <div><Label>Servicing %</Label><Input type="number" step="0.1" value={form.servicing_fee_rate} onChange={(e) => setForm(f => ({ ...f, servicing_fee_rate: e.target.value }))} /></div>
                    <div><Label title="Management fee (2% upcharge), charged up front on its own line">Mgmt fee %</Label><Input type="number" step="0.01" min="0" value={form.management_fee_pct} onChange={(e) => setForm(f => ({ ...f, management_fee_pct: e.target.value }))} placeholder={comp?.is_iso ? '2 (ISO deal)' : '0'} /></div>
                    <div><Label>Role</Label>
                      <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PARTNER">Partner (fee deducted)</SelectItem>
                          <SelectItem value="INVESTOR_FEE">Investor with origination entitlement (fees to ledger)</SelectItem>
                          <SelectItem value="INVESTOR">Clean investor (no fee)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <span className="text-xs text-slate-400 self-center">losses shared by all</span>
                  </div>
                  <Button size="sm" onClick={addInvitee} disabled={busy}><Plus className="w-4 h-4 mr-1" /> add invitee</Button>
                </CardContent>
              </Card>
            )}

            <Card><CardHeader><CardTitle>Allocation</CardTitle></CardHeader>
              <CardContent>
                {comp && (
                  <p className="text-sm mb-2">
                    Finalized {pct(comp.total_final_pct)} of {pct(comp.available_pct)} · PFS retains {pct(comp.pfs_pct)} · total wire-in {formatCurrency(comp.total_wire_in)}
                    {comp.oversubscribed && <span className="text-amber-600"> · oversubscribed</span>}
                  </p>
                )}
                <Table>
                  <TableHeader><TableRow><TableHead>Participant</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Final %</TableHead><TableHead className="text-right">Capital</TableHead><TableHead className="text-right">Commission</TableHead><TableHead className="text-right">Fee</TableHead><TableHead className="text-right">Mgmt fee</TableHead><TableHead className="text-right">Wire-in</TableHead><TableHead className="text-right">RTR</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(offer.invitations || []).map(inv => {
                      const row = (comp?.participants || []).find(p => p.syndicator_id === inv.syndicator_id) || {};
                      const feeCell = row.fee_deducted ? '-' + formatCurrency(row.fee_deducted) : (row.fee_to_ledger ? '+' + formatCurrency(row.fee_to_ledger) + ' ldgr' : '$0');
                      const sheetOpen = !!openSheet[inv.id];
                      return (
                        <React.Fragment key={inv.id}>
                        <TableRow>
                          <TableCell>
                            <button type="button" className="inline-flex items-center gap-1 text-left hover:underline" data-testid={`sheet-toggle-${inv.id}`}
                                    title="Show the participation sheet" onClick={() => setOpenSheet(o => ({ ...o, [inv.id]: !o[inv.id] }))}>
                              {sheetOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                              {inv.syndicator_name || inv.syndicator_id}
                            </button>
                          </TableCell>
                          <TableCell className="text-xs">{(row.role || '').replace('_', ' ').toLowerCase()}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[inv.status] || ''}>{inv.status}</Badge>
                            {inv.status === 'ADJUSTED' && (inv.adjustment_status || 'pending') === 'pending' && (
                              <div className="mt-1 flex items-center gap-1 text-xs" data-testid={`adjustment-${inv.id}`}>
                                <span className="text-amber-700">asks {pct(inv.adjusted_pct)} (proposed {pct(inv.allocated_pct)})</span>
                                <Button size="sm" className="h-6 px-2" disabled={busy} onClick={() => decideAdjustment(inv, 'approve')}>Approve</Button>
                                <Button size="sm" variant="outline" className="h-6 px-2" disabled={busy} onClick={() => decideAdjustment(inv, 'decline')}>Decline</Button>
                              </div>
                            )}
                            {inv.adjustment_status === 'approved' && <div className="text-xs text-emerald-700 mt-1">adjustment approved · {pct(inv.adjusted_pct)}</div>}
                            {inv.adjustment_status === 'declined' && <div className="text-xs text-slate-500 mt-1">adjustment to {pct(inv.adjusted_pct)} declined · stays {pct(inv.allocated_pct)}</div>}
                          </TableCell>
                          <TableCell className="text-right">{pct(row.final_pct)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.capital_share)}</TableCell>
                          <TableCell className="text-right">+{formatCurrency(row.commission_share)}</TableCell>
                          <TableCell className="text-right">{feeCell}</TableCell>
                          <TableCell className="text-right">{row.iso_charge ? `+${formatCurrency(row.iso_charge)} (${row.management_fee_pct}%)` : '—'}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(row.wire_in)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(row.rtr_target)}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {offer.status !== 'CONFIRMED' && (
                              <>
                                <Button variant="outline" size="sm" className="h-7 px-2 mr-1" disabled={busy} onClick={() => sendTo([inv])}
                                        title={inv.last_emailed_at ? `last emailed ${new Date(inv.last_emailed_at).toLocaleString()}` : 'not emailed yet'}
                                        data-testid={`send-${inv.id}`}>
                                  <Send className="w-3 h-3 mr-1" /> {inv.email_count ? `Resend (×${inv.email_count})` : 'Send'}
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => removeInvitee(inv.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                              </>
                            )}
                            {offer.status === 'CONFIRMED' && row.final_pct > 0 && (
                              <Button variant="outline" size="sm" className="h-7 px-2" disabled={busy} onClick={() => resendConfirmations([inv])}
                                      title={inv.confirmation_emailed_at ? `receipt last emailed ${new Date(inv.confirmation_emailed_at).toLocaleString()}` : 'receipt not emailed yet'}
                                      data-testid={`resend-receipt-${inv.id}`}>
                                <Send className="w-3 h-3 mr-1" /> {inv.confirmation_email_count ? `Resend receipt (×${inv.confirmation_email_count})` : 'Send receipt'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        {sheetOpen && (
                          <TableRow className="bg-slate-50">
                            <TableCell colSpan={11} className="py-3">
                              <ParticipationSheet computation={comp} participant={row} terms={inv.terms} name={inv.syndicator_name || inv.syndicator_id} />
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      );
                    })}
                    {comp && (
                      <TableRow className="text-slate-500">
                        <TableCell>PFS (funder)</TableCell><TableCell>—</TableCell><TableCell>—</TableCell>
                        <TableCell className="text-right">{pct(comp.pfs_pct)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(comp.pfs_capital)}</TableCell>
                        <TableCell className="text-right">—</TableCell><TableCell className="text-right">—</TableCell><TableCell className="text-right">—</TableCell>
                        <TableCell className="text-right">—</TableCell><TableCell className="text-right">—</TableCell><TableCell></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {offer.status !== 'CONFIRMED' && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={sendRequests} disabled={busy || !(offer.invitations || []).length}><Send className="w-4 h-4 mr-1" /> send requests</Button>
                <Button variant="outline" onClick={resetAllocation} data-testid="reset-allocation"
                        disabled={busy || !(offer.invitations || []).some(i => i.status !== 'DRAFT' || i.email_count)}
                        title="Clear every answer and link, keep the invitees and proposed %, send again">
                  <RotateCcw className="w-4 h-4 mr-1" /> reset allocation{offer.reset_count ? ` (×${offer.reset_count})` : ''}
                </Button>
                <Button onClick={confirmOffer} disabled={busy || !(offer.invitations || []).length}><CheckCircle className="w-4 h-4 mr-1" /> confirm offering</Button>
              </div>
            )}
            {offer.status === 'CONFIRMED' && (
              <div className="flex items-center justify-between gap-2">
                <p className="text-emerald-700 text-sm flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Offering confirmed — syndications created and ledger opened.</p>
                <Button variant="outline" size="sm" onClick={() => resendConfirmations()} disabled={busy} data-testid="resend-all-receipts"><Send className="w-4 h-4 mr-1" /> Resend all receipts</Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
